// src/web/components/ErrorBoundary.tsx
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { QueryErrorResetBoundary } from '@tanstack/react-query'
import { type FallbackProps, ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary'
import { Button } from '@/components/ui/button'

interface QueryBoundaryProps {
	children: React.ReactNode
	fallback?: React.ComponentType<FallbackProps>
}

function DefaultErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
	return (
		<div className="flex h-full min-h-[200px] flex-col items-center justify-center p-8">
			<div className="mb-4 flex justify-center">
				<div className="relative">
					<div className="absolute inset-0 bg-destructive/10 rounded-full blur-2xl opacity-50" />
					<div className="relative w-16 h-16 rounded-full bg-destructive/5 border border-destructive/20 flex items-center justify-center">
						<ExclamationTriangleIcon className="w-8 h-8 text-destructive" />
					</div>
				</div>
			</div>
			<h2 className="text-lg font-semibold text-foreground mb-2">Something went wrong</h2>
			<p className="text-sm text-muted-foreground mb-4 text-center max-w-md">
				{error instanceof Error ? error.message : 'An unexpected error occurred'}
			</p>
			<Button variant="outline" size="sm" onClick={resetErrorBoundary} className="gap-2">
				<ArrowPathIcon className="w-4 h-4" />
				Try again
			</Button>
		</div>
	)
}

/**
 * QueryBoundary wraps children with QueryErrorResetBoundary and ErrorBoundary.
 * This allows React Query errors to be caught and reset properly.
 */
export function QueryBoundary({ children, fallback: FallbackComponent }: QueryBoundaryProps) {
	return (
		<QueryErrorResetBoundary>
			{({ reset }) => (
				<ReactErrorBoundary
					onReset={reset}
					FallbackComponent={FallbackComponent ?? DefaultErrorFallback}
				>
					{children}
				</ReactErrorBoundary>
			)}
		</QueryErrorResetBoundary>
	)
}

/**
 * AppErrorBoundary is for catching errors at the app root level.
 * Shows a full-page error state with option to reload.
 */
function FullPageErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
	return (
		<div className="min-h-screen flex items-center justify-center bg-background px-4">
			<div className="max-w-md w-full text-center">
				<div className="mb-6 flex justify-center">
					<div className="relative">
						<div className="absolute inset-0 bg-destructive/10 rounded-full blur-3xl opacity-50" />
						<div className="relative w-24 h-24 rounded-full bg-destructive/5 border border-destructive/20 flex items-center justify-center">
							<ExclamationTriangleIcon className="w-12 h-12 text-destructive" />
						</div>
					</div>
				</div>
				<h1 className="text-2xl font-bold text-foreground mb-2">Something went wrong</h1>
				<p className="text-muted-foreground mb-2">
					{error instanceof Error ? error.message : 'An unexpected error occurred'}
				</p>
				<p className="text-sm text-muted-foreground mb-8">Please try again or refresh the page.</p>
				<div className="flex gap-3 justify-center">
					<Button variant="outline" onClick={resetErrorBoundary} className="gap-2">
						<ArrowPathIcon className="w-4 h-4" />
						Try again
					</Button>
					<Button variant="default" onClick={() => window.location.reload()}>
						Refresh page
					</Button>
				</div>
			</div>
		</div>
	)
}

export function AppErrorBoundary({ children }: { children: React.ReactNode }) {
	return (
		<ReactErrorBoundary FallbackComponent={FullPageErrorFallback}>{children}</ReactErrorBoundary>
	)
}
